import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { CurrencyDollar, ShoppingCart, Package, Wallet, TrendUp, Calculator } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#002FA7', '#16A34A', '#EAB308', '#DC2626', '#8B5CF6', '#EC4899'];

export const Dashboard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [metrics, setMetrics] = useState(null);
  const [salesChart, setSalesChart] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [salesBySeller, setSalesBySeller] = useState([]);
  const [currentCut, setCurrentCut] = useState(null);
  const [overdue, setOverdue] = useState(null);

  useEffect(() => {
    // Cargas paralelas independientes — cada card se hidrata cuando llega su data,
    // sin bloquear el render con un loader full-page (más rápido percibido).
    axios.get(`${API}/dashboard/metrics`, { withCredentials: true })
      .then(({ data }) => setMetrics(data))
      .catch(() => toast.error('Error al cargar métricas'));
    axios.get(`${API}/dashboard/sales-chart`, { withCredentials: true })
      .then(({ data }) => setSalesChart(data))
      .catch(() => { /* */ });
    axios.get(`${API}/reports/top-products`, { withCredentials: true })
      .then(({ data }) => setTopProducts(data.slice(0, 5)))
      .catch(() => { /* */ });
    axios.get(`${API}/reports/sales`, { withCredentials: true })
      .then(({ data }) => setSalesBySeller(data))
      .catch(() => { /* */ });
    axios.get(`${API}/cuts/current`, { withCredentials: true })
      .then(({ data }) => setCurrentCut(data))
      .catch(() => setCurrentCut(null));
    axios.get(`${API}/dashboard/overdue-shipments`, { withCredentials: true })
      .then(({ data }) => setOverdue(data))
      .catch(() => setOverdue(null));
  }, []);


  return (
    <MainLayout>
      <div className="p-8 space-y-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Resumen general de tu negocio</p>
        </div>

        {currentCut && (
          <Link to="/cuts" className="block">
            <Card className="bg-gradient-to-r from-violet-50 to-emerald-50 border-violet-200 hover:shadow-lg transition-shadow" data-testid="dashboard-cut-widget">
              <CardContent className="py-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-violet-600 text-white flex items-center justify-center">
                  <Calculator size={26} weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold">Corte abierto: {currentCut.folio}</span>
                    <Badge className="bg-emerald-600">Activo</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Desde {new Date(currentCut.started_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })} ·
                    {' '}{currentCut.totals?.sales_count || 0} ventas
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-violet-700">
                    ${(currentCut.totals?.sales_total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-xs text-muted-foreground">total vendido</div>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}

        {overdue && overdue.count > 0 && (
          <Link to="/shipments" className="block" data-testid="dashboard-overdue-widget">
            <Card className={`border-2 hover:shadow-lg transition-shadow ${overdue.count > 5 ? 'bg-rose-50 border-rose-400' : 'bg-amber-50 border-amber-400'}`}>
              <CardContent className="py-4 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full text-white flex items-center justify-center text-2xl ${overdue.count > 5 ? 'bg-rose-600' : 'bg-amber-600'}`}>
                  ⏰
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold">
                      {overdue.count} envío{overdue.count === 1 ? '' : 's'} por cobrar hace &gt;{overdue.days_threshold} días
                    </span>
                    {overdue.count > 5 && <Badge className="bg-rose-600">Urgente</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {overdue.top.slice(0, 3).map(sh => `${sh.folio} (${sh.days_late}d)`).join(' · ')}
                    {overdue.count > 3 && ` · +${overdue.count - 3} más`}
                  </p>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold ${overdue.count > 5 ? 'text-rose-700' : 'text-amber-700'}`}>
                    {overdue.count}
                  </div>
                  <div className="text-xs text-muted-foreground">atrasos</div>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="metric-sales-today">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Ventas Hoy</CardTitle>
              <ShoppingCart size={20} className="text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{metrics?.sales_today || 0}</div>
              <p className="text-sm text-success mt-1">
                ${(metrics?.revenue_today || 0).toFixed(2)}
              </p>
            </CardContent>
          </Card>

          {isAdmin && (
            <Card data-testid="metric-sales-month">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Ventas del Mes</CardTitle>
                <TrendUp size={20} className="text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{metrics?.sales_month || 0}</div>
                <p className="text-sm text-success mt-1">
                  ${(metrics?.revenue_month || 0).toFixed(2)}
                </p>
              </CardContent>
            </Card>
          )}

          <Card data-testid="metric-low-stock">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Stock Bajo</CardTitle>
              <Package size={20} className="text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-warning">{metrics?.low_stock_count || 0}</div>
              <p className="text-sm text-muted-foreground mt-1">Productos</p>
            </CardContent>
          </Card>

          <Card data-testid="metric-pending-commissions">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Comisiones Pendientes</CardTitle>
              <Wallet size={20} className="text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">
                ${(metrics?.pending_commissions || 0).toFixed(2)}
              </div>
              <p className="text-sm text-muted-foreground mt-1">Por pagar</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Ventas Últimos 7 Días</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={salesChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" stroke="#94A3B8" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#94A3B8" style={{ fontSize: '12px' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '4px'
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" name="Ingresos ($)" stroke="#002FA7" strokeWidth={2} />
                  <Line type="monotone" dataKey="sales" name="Ventas" stroke="#16A34A" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Top Products Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Top 5 Productos</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topProducts}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="product_name" stroke="#94A3B8" style={{ fontSize: '11px' }} />
                  <YAxis stroke="#94A3B8" style={{ fontSize: '12px' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '4px'
                    }}
                  />
                  <Bar dataKey="quantity_sold" name="Cantidad Vendida" fill="#002FA7" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Sales by Seller Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Ventas por Vendedor</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={(() => {
                    // Top 8 + "Otros" para no saturar
                    const sorted = [...salesBySeller].sort((a, b) => (b.total_revenue || 0) - (a.total_revenue || 0));
                    if (sorted.length <= 9) return sorted;
                    const top = sorted.slice(0, 8);
                    const otros = sorted.slice(8).reduce((acc, s) => acc + (s.total_revenue || 0), 0);
                    return [...top, { seller_name: 'Otros', total_revenue: otros }];
                  })()}
                  dataKey="total_revenue"
                  nameKey="seller_name"
                  cx="40%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={40}
                  paddingAngle={2}
                >
                  {salesBySeller.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '4px' }}
                  formatter={(value, name) => [`$${Number(value).toFixed(0)}`, name]}
                />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  wrapperStyle={{ fontSize: '12px', paddingLeft: '12px', maxWidth: '45%' }}
                  formatter={(value, entry) => (
                    <span style={{ color: '#334155' }}>
                      {value} <span style={{ color: '#94a3b8' }}>· ${(entry?.payload?.total_revenue || 0).toFixed(0)}</span>
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};
